import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Popover from '@mui/material/Popover';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs, { Dayjs } from 'dayjs';
import { alpha } from '@mui/material/styles';
import RtlButton from './RtlButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from './CustomNotification';
import type { PaymentPayload, OnePaymentPayload, InstallmentsPaymentPayload, InstallmentPaymentItem, PaymentMethodType } from '../data/orders';
import { payOrder, uploadPaymentReceipt } from '../data/orders';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface PaymentDialogPayload {
  orderId: number;
  // full_amount and pending_amount are optional on an order, so these can be null.
  totalAmount: number | null;
  pendingAmount: number | null;
  orderStatus: 'CREATED' | 'BOOKED' | 'CANCELED' | 'COMPLETED';
}

interface PaymentDialogProps {
  open: boolean;
  payload: PaymentDialogPayload | null;
  onClose: () => void;
}

export default function PaymentDialog({ open, payload, onClose }: PaymentDialogProps) {
  const { t, direction } = useLanguage();
  const notifications = useNotification();
  const isRtl = direction === 'rtl';
  const [tabValue, setTabValue] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // The dialog stays mounted while closed, so its body runs even for orders
  // without a full_amount. Normalise the payload amount to keep it safe.
  const totalAmount = Number(payload?.totalAmount ?? 0);

  // One Payment State
  const [onePaymentAmount, setOnePaymentAmount] = React.useState<string>('');
  const [onePaymentMethod, setOnePaymentMethod] = React.useState<PaymentMethodType>('CASH');
  const [onePaymentDate, setOnePaymentDate] = React.useState<Dayjs>(dayjs());
  const [datePickerAnchor, setDatePickerAnchor] = React.useState<HTMLElement | null>(null);
  const [installmentDateAnchors, setInstallmentDateAnchors] = React.useState<Record<number, HTMLElement | null>>({});

  // Installments State
  const [installments, setInstallments] = React.useState<InstallmentPaymentItem[]>([
    {
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
      amount: 0,
      paid: false,
      payment_method: 'CASH',
    },
  ]);

  // Image Upload State (only for CREATED status)
  const [receiptImage, setReceiptImage] = React.useState<File | null>(null);


  React.useEffect(() => {
    if (open && payload && payload.totalAmount !== undefined && payload.totalAmount !== null) {
      // Reset form when dialog opens
      // For one payment, use totalAmount (full_amount) instead of pendingAmount
      setOnePaymentAmount(payload.totalAmount.toString());
      setOnePaymentMethod('CASH');
      setOnePaymentDate(dayjs());
      setInstallments([
        {
          due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
          amount: payload.totalAmount,
          paid: true, // First installment is paid by default
          payment_method: 'CASH',
        },
      ]);
      setReceiptImage(null);
    }
  }, [open, payload]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOnePaymentAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOnePaymentAmount(event.target.value);
  };

  const handleOnePaymentMethodChange = (event: SelectChangeEvent) => {
    setOnePaymentMethod(event.target.value as PaymentMethodType);
  };

  const handleAddInstallment = () => {
    if (!payload) return;

    const newCount = installments.length + 1;
    const splitAmount = parseFloat((totalAmount / newCount).toFixed(2));
    const lastInstallment = installments[installments.length - 1];
    const inheritedMethod = lastInstallment?.payment_method ?? 'CASH';

    // Redistribute amounts evenly across all existing + new installment
    const updatedInstallments = installments.map((inst, i) => ({
      ...inst,
      amount: i === installments.length - 1
        ? parseFloat((totalAmount - splitAmount * (newCount - 1)).toFixed(2))
        : splitAmount,
    }));

    setInstallments([
      ...updatedInstallments,
      {
        due_date: dayjs().add(newCount, 'month').format('YYYY-MM-DD'),
        amount: splitAmount,
        paid: false,
        payment_method: inheritedMethod,
      },
    ]);
  };

  const handleRemoveInstallment = (index: number) => {
    setInstallments(installments.filter((_, i) => i !== index));
  };

  const handleInstallmentChange = (
    index: number,
    field: keyof InstallmentPaymentItem,
    value: string | number | boolean | Dayjs | null,
  ) => {
    const updated = [...installments];
    if (field === 'due_date') {
      if (dayjs.isDayjs(value)) {
        updated[index][field] = value.format('YYYY-MM-DD');
      } else if (typeof value === 'string') {
        updated[index][field] = value;
      }
    } else if (field === 'amount') {
      updated[index][field] = typeof value === 'string' ? parseFloat(value) || 0 : value as number;
    } else if (field === 'paid') {
      updated[index][field] = value as boolean;
    } else if (field === 'payment_method') {
      updated[index][field] = value as PaymentMethodType | undefined;
    }
    setInstallments(updated);
  };

  const handleImageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        setReceiptImage(files[0]);
      }
    },
    [],
  );

  const handleRemoveImage = React.useCallback(() => {
    setReceiptImage(null);
  }, []);

  const handleSubmit = async () => {
    if (!payload) return;

    setIsSubmitting(true);
    try {
      let paymentPayload: PaymentPayload;

      if (tabValue === 0) {
        // One Payment
        const amount = parseFloat(onePaymentAmount);
        if (!amount || amount <= 0) {
          throw new Error(t('orders.amountRequired'));
        }
        // Validate that amount equals totalAmount
        if (amount !== totalAmount) {
          throw new Error(t('orders.onePaymentMustEqualTotal'));
        }
        paymentPayload = {
          type: 'one_payment',
          amount,
          payment_method: onePaymentMethod,
        } as OnePaymentPayload;
      } else {
        // Installments
        // Filter out installments with amount 0
        const validInstallments = installments.filter((inst) => inst.amount > 0);

        if (validInstallments.length === 0) {
          throw new Error(t('orders.installmentRequired'));
        }
        const totalInstallments = validInstallments.reduce((sum, inst) => sum + inst.amount, 0);
        if (totalInstallments <= 0) {
          throw new Error(t('orders.installmentAmountRequired'));
        }
        // Validate that total installments equal totalAmount
        if (Math.abs(totalInstallments - totalAmount) > 0.01) {
          throw new Error(t('orders.installmentsTotalMustEqualFullAmount'));
        }
        paymentPayload = {
          type: 'installments',
          installments: validInstallments.map((inst) => ({
            due_date: inst.due_date,
            amount: inst.amount,
            paid: inst.paid || false,
            ...(inst.paid && inst.payment_method ? { payment_method: inst.payment_method } : {}),
          })),
        } as InstallmentsPaymentPayload;
      }

      // Upload receipt image first if order status is CREATED and image is provided
      if (payload.orderStatus === 'CREATED' && receiptImage) {
        await uploadPaymentReceipt(payload.orderId, receiptImage);
      }

      // Proceed with payment only if image upload succeeded (or no image)
      await payOrder(payload.orderId, paymentPayload);

      notifications.show(t('orders.paymentSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      onClose();
    } catch (error) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(error as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalInstallmentsAmount = installments.reduce((sum, inst) => sum + inst.amount, 0);

  if (!payload) {
    return null;
  }

  return (
    <Dialog
      open={open && !!payload}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        component: 'form',
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        },
        sx: (theme) => ({
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(2, 139, 203, 0.15)',
          background: theme.palette.background.paper,
          ...theme.applyStyles('dark', {
            background: alpha('#1a1a1a', 0.95),
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }),
        }),
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 3,
          px: 3,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          fontWeight: 600,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
        }}
      >
        {t('orders.payNow')}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
        <Box sx={(theme) => ({
          borderBottom: 2,
          borderColor: 'primary.light',
          mb: 3,
          ...theme.applyStyles('dark', {
            borderColor: alpha('#028bcb', 0.3),
          }),
        })}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="payment type tabs"
            TabIndicatorProps={{
              sx: {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }
            }}
            sx={(theme) => ({
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                textTransform: 'none',
                minHeight: { xs: 48, sm: 56 },
                marginTop: '5px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            })}
          >
            <Tab label={t('orders.onePayment')} />
            <Tab label={t('orders.installments')} />
          </Tabs>
        </Box>

        <Fade in={tabValue === 0} timeout={300}>
          <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
            {/* One Payment Form */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('orders.amount')}
                  type="number"
                  fullWidth
                  required
                  value={onePaymentAmount}
                  onChange={handleOnePaymentAmountChange}
                  inputProps={{ min: 0, step: 0.01, readOnly: true }}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(2, 139, 203, 0.1)',
                      },
                    }
                  }}
                  InputLabelProps={{
                    sx: {
                      textAlign: isRtl ? 'right' : 'left',
                      right: isRtl ? 0 : 'auto',
                      left: isRtl ? 'auto' : 0,
                      fontWeight: 500,
                    }
                  }}
                  helperText={t('orders.onePaymentAmountHelper')}
                  FormHelperTextProps={{
                    sx: { textAlign: isRtl ? 'right' : 'left' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel
                    sx={{
                      textAlign: isRtl ? 'right' : 'left',
                      right: isRtl ? 0 : 'auto',
                      left: isRtl ? 'auto' : 0,
                      fontWeight: 500,
                    }}
                  >
                    {t('orders.paymentMethod')}
                  </InputLabel>
                  <Select
                    value={onePaymentMethod}
                    onChange={handleOnePaymentMethodChange}
                    label={t('orders.paymentMethod')}
                    sx={{
                      backgroundColor: 'transparent',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(2, 139, 203, 0.1)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    }}
                  >
                    <MenuItem value="CASH">{t('orders.cash')}</MenuItem>
                    <MenuItem value="CARD">{t('orders.card')}</MenuItem>
                    <MenuItem value="BANK_TRANSFER">{t('orders.bankTransfer')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  size="medium"
                  label={t('orders.paymentDate')}
                  value={onePaymentDate.format('MMM DD, YYYY')}
                  onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton
                        size="small"
                        edge="end"
                        sx={{ backgroundColor: 'transparent', border: 0, '&:hover': { backgroundColor: 'transparent' } }}
                      >
                        <CalendarTodayIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                  InputLabelProps={{
                    sx: {
                      textAlign: isRtl ? 'right' : 'left',
                      right: isRtl ? 0 : 'auto',
                      left: isRtl ? 'auto' : 0,
                      fontWeight: 500,
                    },
                  }}
                  fullWidth
                  sx={{
                    cursor: 'pointer',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                    '& input': { cursor: 'pointer' },
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Popover
                    open={Boolean(datePickerAnchor)}
                    anchorEl={datePickerAnchor}
                    onClose={() => setDatePickerAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
                  >
                    <Box sx={{ p: 1 }}>
                      <DateCalendar
                        value={onePaymentDate}
                        onChange={(newValue: Dayjs | null) => {
                          if (newValue) {
                            setOnePaymentDate(newValue);
                            setDatePickerAnchor(null);
                          }
                        }}
                        sx={{
                          '& .MuiPickersCalendarHeader-root': {
                            '& .MuiPickersArrowSwitcher-root': {
                              display: 'flex',
                              gap: 3,
                              '& .MuiPickersArrowSwitcher-button:first-of-type': {
                                order: isRtl ? 2 : 1,
                              },
                              '& .MuiPickersArrowSwitcher-button:last-of-type': {
                                order: isRtl ? 1 : 2,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Popover>
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        <Fade in={tabValue === 1} timeout={300}>
          <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
            {/* Installments Form */}
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2,
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.light}15)`,
                border: '1px solid',
                borderColor: 'primary.light',
                ...theme.applyStyles('dark', {
                  background: alpha('#028bcb', 0.1),
                  borderColor: alpha('#028bcb', 0.3),
                }),
              })}
            >
              <Box>
                <Typography variant="body1" fontWeight={600} color="primary.main" gutterBottom>
                  {t('orders.totalInstallments')}: {totalInstallmentsAmount.toFixed(2)} {t('orders.currency')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.requiredAmount')}: {totalAmount.toFixed(2)} {t('orders.currency')}
                </Typography>
                {Math.abs(totalInstallmentsAmount - totalAmount) > 0.01 && (
                  <Typography variant="caption" color="error" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>
                    {t('orders.installmentsTotalMustEqualFullAmount')}
                  </Typography>
                )}
              </Box>
              <RtlButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddInstallment}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(2, 139, 203, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {t('orders.addInstallment')}
              </RtlButton>
            </Box>
            {installments.map((installment, index) => (
              <Box
                key={index}
                sx={(theme) => ({
                  border: '2px solid',
                  borderColor: 'primary.light',
                  borderRadius: 2,
                  p: { xs: 2, sm: 2.5 },
                  mb: 2,
                  background: theme.palette.background.paper,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(2, 139, 203, 0.15)',
                    borderColor: 'primary.main',
                  },
                  ...theme.applyStyles('dark', {
                    background: alpha('#1a1a1a', 0.6),
                    borderColor: alpha('#028bcb', 0.3),
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(2, 139, 203, 0.3)',
                      borderColor: alpha('#028bcb', 0.6),
                    },
                  }),
                })}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label={t('orders.dueDate')}
                      value={dayjs(installment.due_date).format('MMM DD, YYYY')}
                      onClick={(e) => setInstallmentDateAnchors((prev) => ({ ...prev, [index]: e.currentTarget }))}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton size="small" edge="end" sx={{ backgroundColor: 'transparent', border: 0, '&:hover': { backgroundColor: 'transparent' } }}>
                            <CalendarTodayIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                      InputLabelProps={{
                        sx: {
                          textAlign: isRtl ? 'right' : 'left',
                          right: isRtl ? 0 : 'auto',
                          left: isRtl ? 'auto' : 0,
                          fontWeight: 500,
                        },
                      }}
                      fullWidth
                      required
                      sx={{
                        cursor: 'pointer',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          cursor: 'pointer',
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                        '& input': { cursor: 'pointer' },
                      }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Popover
                        open={Boolean(installmentDateAnchors[index])}
                        anchorEl={installmentDateAnchors[index]}
                        onClose={() => setInstallmentDateAnchors((prev) => ({ ...prev, [index]: null }))}
                        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'right' : 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
                      >
                        <Box sx={{ p: 1 }}>
                          <DateCalendar
                            value={dayjs(installment.due_date)}
                            onChange={(newValue: Dayjs | null) => {
                              if (newValue) {
                                handleInstallmentChange(index, 'due_date', newValue);
                                setInstallmentDateAnchors((prev) => ({ ...prev, [index]: null }));
                              }
                            }}
                            sx={{
                              '& .MuiPickersCalendarHeader-root': {
                                '& .MuiPickersArrowSwitcher-root': {
                                  display: 'flex',
                                  gap: 3,
                                  '& .MuiPickersArrowSwitcher-button:first-of-type': { order: isRtl ? 2 : 1 },
                                  '& .MuiPickersArrowSwitcher-button:last-of-type': { order: isRtl ? 1 : 2 },
                                },
                              },
                            }}
                          />
                        </Box>
                      </Popover>
                    </LocalizationProvider>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <TextField
                      label={t('orders.amount')}
                      type="number"
                      fullWidth
                      required
                      value={installment.amount}
                      onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputLabelProps={{
                        sx: {
                          textAlign: isRtl ? 'right' : 'left',
                          right: isRtl ? 0 : 'auto',
                          left: isRtl ? 'auto' : 0,
                          fontWeight: 500,
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={installment.paid || false}
                          onChange={(e) => handleInstallmentChange(index, 'paid', e.target.checked)}
                        />
                      }
                      label={t('orders.paid')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel
                        sx={{
                          textAlign: isRtl ? 'right' : 'left',
                          right: isRtl ? 0 : 'auto',
                          left: isRtl ? 'auto' : 0,
                          fontWeight: 500,
                        }}
                      >
                        {t('orders.paymentMethod')}
                      </InputLabel>
                      <Select
                        value={installment.payment_method || ''}
                        onChange={(e) => handleInstallmentChange(index, 'payment_method', e.target.value)}
                        label={t('orders.paymentMethod')}
                        sx={{
                          backgroundColor: 'transparent',
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                          },
                        }}
                      >
                        <MenuItem value="CASH">{t('orders.cash')}</MenuItem>
                        <MenuItem value="CARD">{t('orders.card')}</MenuItem>
                        <MenuItem value="BANK_TRANSFER">{t('orders.bankTransfer')}</MenuItem>
                        <MenuItem value="CHEQUE">{t('orders.cheque')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 1 }}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveInstallment(index)}
                      disabled={installments.length === 1}
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Fade>

        {/* Image Upload Section - Only for CREATED status */}
        {payload.orderStatus === 'CREATED' && (
          <Box
            sx={(theme) => ({
              mt: 4,
              p: 3,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}05, ${theme.palette.primary.light}10)`,
              border: '1px dashed',
              borderColor: 'primary.main',
              ...theme.applyStyles('dark', {
                background: alpha('#028bcb', 0.08),
                borderColor: alpha('#028bcb', 0.4),
              }),
            })}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'primary.main',
                fontSize: { xs: '1rem', sm: '1.125rem' },
              }}
            >
              {t('orders.uploadReceipt')}
            </Typography>

            <RtlButton
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              sx={{
                mb: 2,
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: 'primary.50',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(2, 139, 203, 0.2)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {t('orders.addReceiptImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </RtlButton>

            {/* Image Preview */}
            {receiptImage && (
              <Fade in={!!receiptImage}>
                <Card
                  sx={{
                    maxWidth: 400,
                    borderRadius: 2,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={URL.createObjectURL(receiptImage)}
                    alt="Receipt Preview"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleRemoveImage}
                      aria-label="delete"
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Fade>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: theme.palette.background.default,
          ...theme.applyStyles('dark', {
            background: alpha('#0a0a0a', 0.8),
            borderColor: alpha('#ffffff', 0.1),
          }),
        })}
      >
        <Button
          onClick={() => onClose()}
          disabled={isSubmitting}
          sx={(theme) => ({
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : 'grey.100',
            },
          })}
        >
          {t('common.cancel')}
        </Button>
        <RtlButton
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={{
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark}) !important`,
            boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3) !important',
            border: '0 !important',
            borderRadius: 2,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': {
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main}) !important`,
              boxShadow: '0 6px 16px rgba(2, 139, 203, 0.4) !important',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {t('orders.submitPayment')}
        </RtlButton>
      </DialogActions>
    </Dialog>
  );
}

